import React from "react";
import { Menu, Transition } from "@headlessui/react";

interface MenuOption {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  className?: string;
  onClick: () => void;
}

interface MenuSectionProps {
  options: MenuOption[];
}

interface ProfileDropdownProps {
  imageUrl?: string;
  altText: string;
  sections: MenuSectionProps[];
  icon?: React.ReactNode;
  menuItemsClassName?: string;
}

const MenuSection: React.FC<MenuSectionProps> = ({ options }) => (
  <div className="p-1 w-full">
    {options.map((option, index) => (
      <Menu.Item key={index}>
        {({ active }) => (
          <button
            type="button"
            className={`${
              active ? "bg-gray-100 text-gray-900" : "text-gray-700"
            } group flex w-full items-center rounded-md px-4 py-2 text-sm ${option.className || ""}`}
            onClick={option.onClick}
          >
            {option.icon && (
              <option.icon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            )}
            {option.label}
          </button>
        )}
      </Menu.Item>
    ))}
  </div>
);

const CustomMenuDropdown: React.FC<ProfileDropdownProps> = ({
  icon,
  imageUrl,
  altText,
  sections,
  menuItemsClassName,
}) => {
  const validSections = Array.isArray(sections) ? sections : [];

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="flex items-center rounded-full bg-transparent text-gray-500 hover:text-gray-600 focus:outline-none text-sm">
          <span className="sr-only">Open options</span>
          {imageUrl ? (
            <img
              className="h-6 w-6 rounded-full"
              src={imageUrl}
              alt={altText}
            />
          ) : (
            icon && (
              <span className="h-6 w-6 flex items-center justify-center">
                {icon}
              </span>
            )
          )}
        </Menu.Button>
      </div>

      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        {/* Changes made here:
            1. Added 'absolute' to take it out of flow.
            2. Added 'right-0' to align right edge of dropdown with button.
            3. Added 'z-50' to ensure it sits on top of other table rows.
            4. Added 'w-48' (or w-56) for fixed width.
            5. 'origin-top-right' ensures it scales from the button's corner.
         */}
        <Menu.Items
          className={`absolute right-0 text-normal z-50 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${menuItemsClassName || ""}`}
        >
          {validSections.map((section, index) => (
            <MenuSection key={index} options={section.options} />
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default CustomMenuDropdown;
